import { useMemo } from 'react';

import type { PartialLineItem } from '../../../../../../graphql';

// ─────────────────────────────────────────────────────────────────────────────

export const useFormattedBagLineItems = (params: Params) => {
  const { isGroupOrder, lineItems } = params;

  // ─── Derived ──────────────────────────────────────────────────────────────

  return useMemo(() => {
    if (!isGroupOrder) {
      return {
        lineItemGroups: [{ id: SOLO_ORDER_GROUP_ID, isMe: true, lineItems }],
        shouldShowParticipantHeaders: false,
      };
    }

    return {
      lineItemGroups: getParticipantGroups(lineItems),
      shouldShowParticipantHeaders: true,
    };
  }, [isGroupOrder, lineItems]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getParticipantGroups(lineItems: readonly PartialLineItem[]) {
  const participantGroups = lineItems.reduce<LineItemGroup[]>(
    reduceLineItemsToParticipantGroups,
    [],
  );

  return [...participantGroups].sort(compareParticipantGroups);
}

function reduceLineItemsToParticipantGroups(groups: LineItemGroup[], lineItem: PartialLineItem) {
  const participantId = lineItem.orderParticipant?.id ?? UNASSIGNED_PARTICIPANT_GROUP_ID;
  const existingGroup = groups.find((group) => group.id === participantId);

  if (!existingGroup) {
    return [...groups, createParticipantGroup({ lineItem, participantId })];
  }

  return groups.map((group) => addLineItemToParticipantGroup({ group, lineItem, participantId }));
}

function createParticipantGroup(params: CreateParticipantGroupParams): LineItemGroup {
  const { lineItem, participantId } = params;
  const participant = lineItem.orderParticipant;

  return {
    id: participantId,
    displayName: participant?.displayName,
    isMe: participant?.isMe ?? false,
    lineItems: [lineItem],
  };
}

function addLineItemToParticipantGroup(params: AddLineItemToParticipantGroupParams) {
  const { group, lineItem, participantId } = params;

  if (group.id !== participantId) return group;

  return { ...group, lineItems: [...group.lineItems, lineItem] };
}

function compareParticipantGroups(currentGroup: LineItemGroup, nextGroup: LineItemGroup) {
  return Number(nextGroup.isMe) - Number(currentGroup.isMe);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SOLO_ORDER_GROUP_ID = 'solo-order';
const UNASSIGNED_PARTICIPANT_GROUP_ID = 'unassigned-participant';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  isGroupOrder: boolean;
  lineItems: readonly PartialLineItem[];
};

type LineItemGroup = {
  id: string;
  displayName?: string | null;
  isMe: boolean;
  lineItems: readonly PartialLineItem[];
};

type CreateParticipantGroupParams = {
  lineItem: PartialLineItem;
  participantId: string;
};

type AddLineItemToParticipantGroupParams = {
  group: LineItemGroup;
  lineItem: PartialLineItem;
  participantId: string;
};
